/**
 * This script handles logic for when the experience is embedded in an iframe in a client's site
 *
 * It is imported into our Root.js file and is subsequently used around the codebase (mainly in top-level data components)
 *
 * Messages will be passed between the parent site and embed iframe so our configurator can communicate with the client's site
 */

import { isAllowedShopOrigin } from "./postMessageOrigins";

/**
 * variables
 */

let windowId = null;
let resizeTimer = null;
let hashChangeTimer = null;
const hashChangeCallbacks = [];
let cartResponseCallback = null;
let localStorage = null;
let parentOrigin = null;

/**
 *
 * EmbedController object that is exported to the rest of the codebase
 *
 * Used to send messages to the parent site
 *
 */

const EmbedController = {
  // used around the codebase to know if experience is embedded or not
  isEmbedded: false,

  // full parent URL for saving a config
  parentUrl: "",

  // activates the embed
  activate() {
    this.isEmbedded = true;
    setupMessageListeners();
  },

  /**
   *
   *
   * sending messages to parent
   *
   */

  // send request for hash change to the parent site
  sendHashChange(hash) {
    sendMessageToParent({
      method: "hashchange",
      hash,
    });
  },

  // send request to change full URL of parent site (redirect)
  sendParentUrlChange(url) {
    sendMessageToParent({
      method: "parentUrlChange",
      url,
    });
  },

  // send request to reload the parent site
  sendParentReloadRequest() {
    sendMessageToParent({
      method: "parentReloadRequest",
    });
  },

  // send request to fully refresh the parent site
  sendParentRefreshRequest() {
    sendMessageToParent({
      method: "parentRefreshRequest",
    });
  },

  // send data to add to client site's cart
  sendAddToCart(requestUrl, options) {
    sendMessageToParent({
      method: "addToCart",
      requestUrl,
      options,
    });
  },

  // Parent POSTs the unique-product body to the builder Netlify function.
  // Same method name as Thrill Seekers. Token never enters the iframe.
  sendShopifyProduct(requestUrl, options) {
    return postMessageRequest("createShopifyProductRequest", "createShopifyProductResponse", requestUrl, options);
  },

  // Parent uploads the configuration screenshot as the new product's image.
  sendUploadProductImage(requestUrl, options) {
    return postMessageRequest("uploadProductImageRequest", "uploadProductImageResponse", requestUrl, options);
  },

  // send localStorage data to client site
  sendLocalStorage(action, storageDetails) {
    sendMessageToParent({
      method: "localStorage",
      action, // set, get, remove
      storageDetails, // keyName and time to live data
    });
  },

  // send google analytics data to client site
  sendGTMAnalyticsEvent(data) {
    sendMessageToParent({
      method: "GTMAnalyticsEvent",
      dataLayerObj: data,
    });
  },

  /**
   *
   * callback's for responses from parent
   *
   */

  // listener that fires a cb when a hashchange on the parent occurs
  // we just store cb's in here and then process then in the event listener above
  // Called in UrlDataController.js
  setHashChangeCallback(callback) {
    hashChangeCallbacks.push(callback);
  },

  // when product is added to cart
  setCartResponseCallback(callback) {
    cartResponseCallback = callback;
  },

  // local storage response
  setLocalStorageCallback(callback) {
    localStorage = callback;
  },
};

// if embedded in an iframe
// and not already activated
// activate
if (window.self !== window.top && !EmbedController.isEmbedded) EmbedController.activate();

function sendMessageToParent(message) {
  if (!EmbedController.isEmbedded || !parentOrigin) return;
  window.parent.postMessage(message, parentOrigin);
}

function postMessageRequest(requestMethod, responseMethod, requestUrl, options) {
  return new Promise(function (resolve, reject) {
    if (!parentOrigin) {
      reject("Missing parent origin for " + responseMethod);
      return;
    }

    const timer = setTimeout(function () {
      window.removeEventListener("message", handleResponse);
      reject("Timed out waiting for " + responseMethod);
    }, 20000);

    function handleResponse(e) {
      if (!isAllowedShopOrigin(e.origin)) return;
      if (!e.data || e.data.method !== responseMethod) return;
      clearTimeout(timer);
      window.removeEventListener("message", handleResponse);
      if (e.data.errorMsg) reject(e.data.errorMsg);
      else resolve(e.data.responseData);
    }

    window.addEventListener("message", handleResponse);
    window.parent.postMessage(
      {
        method: requestMethod,
        requestUrl,
        options,
      },
      parentOrigin
    );
  });
}

/**
 *
 * handle messages received from parent
 *
 */

function setupMessageListeners() {
  window.addEventListener("message", function (e) {
    if (!isAllowedShopOrigin(e.origin)) return;
    parentOrigin = e.origin;

    const method = e.data && e.data.method;
    if (!method) {
      return;
    }

    if (messageHandlers[method]) {
      messageHandlers[method](e);
    }
  });
}

const messageHandlers = {
  register: handleRegisterMessageFromParent,

  hashchange: handleHashChangeMessage,

  "request-size": handleRequestSizeMessage,

  cartResponse: handleCartResponseMessage,

  localStorage: handleLocalStorageMessage,
};

function updateParentUrlField(newUrl) {
  EmbedController.parentUrl = newUrl;
}

async function handleRegisterMessageFromParent(e) {
  // window._tt will hold data from client site
  window._tt = e.data;
  windowId = e.data.windowId;

  // initialize parentUrl
  updateParentUrlField(e.data.initialParentUrl);

  // tell's Root.js to inject react code to start experience
  document.dispatchEvent(new CustomEvent("InitEmbed"));

  sendHeight();
  setTimeout(() => {
    sendHeight();
  }, 100);
  setTimeout(() => {
    sendHeight();
  }, 250);
}

// listener is set by onHashChange() above, which is called in UrlDataController.js
function handleHashChangeMessage(e) {
  updateParentUrlField(e.data.fullUrl);
  const hash = e.data.hash;
  clearTimeout(hashChangeTimer);
  hashChangeTimer = setTimeout(() => {
    hashChangeCallbacks.forEach((listener) => {
      listener(hash);
    });
  }, 100);
}

function handleRequestSizeMessage(e) {
  clearTimeout(resizeTimer);
  resizeTimer = setTimeout(function () {
    sendHeight();
  }, 100);
}

function handleCartResponseMessage(e) {
  const { errorMsg, redirectUrl } = e.data;
  cartResponseCallback(errorMsg, redirectUrl);
}

// send's height to client site
function sendHeight() {
  if (windowId) {
    const body = document.body;
    const height = Math.max(body.scrollHeight, body.offsetHeight);
    sendMessageToParent({ method: "resize", windowId: windowId, height: height });
  }
}

function handleLocalStorageMessage(e) {
  const { foundItem } = e.data;

  if (foundItem) localStorage(foundItem);
}

export default EmbedController;
